import {t} from "@ui/common/intl"
import {DependencyList, EffectCallback, useEffect, useRef} from "react"
import {Coordinate} from "ol/coordinate"
import Polyfit from "@ui/common/polyfit"
import {isNumber} from "lodash-es"
import {LineString} from "ol/geom"

export const getCurrentPosition = (): Promise<GeolocationPosition> => {
	return new Promise((resolve, reject) => {
		if (!navigator.geolocation) {
			return reject(new Error(t("error.geolocation.unavailable")))
		}
		navigator.geolocation.getCurrentPosition(
			resolve,
			reject,
			{
			  enableHighAccuracy: true,
			  timeout: 5000,
			  maximumAge: 0,
			}
		)
	})
}

export const delay = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export const posToStr = (pos: GeolocationPosition) => {
	const {timestamp, coords} = pos
	const {latitude, longitude, accuracy, speed} = coords
	return `${timestamp} - (${latitude}, ${longitude}), accuracy=${accuracy}, speed=${speed}`
}

// получение азимута через другую либу, значение немного отличаются от метода getAzimuth.
export const getAzimuth = ([Ax, Ay]: Coordinate, [Bx, By]: Coordinate) => {
	return Math.round((azimuth({lng: Ax, lat: Ay}, {lng: Bx, lat: By}).azimuth / 57.3 + Number.EPSILON) * 100) / 100
}

export const calculateSpeed = (A: GeolocationPosition, B: GeolocationPosition) => {
	const {coords: {longitude: Alng, latitude: Alat} = {}} = A
	const {coords: {longitude: Blng, latitude: Blat} = {}} = B
	if (!Alng || !Alat || !Blng || !Blat) {
    return 0
  }
  const distance = azimuth({lng: Alng, lat: Alat},{lng: Blng, lat: Blat}).distance
	const time = Math.abs(A.timestamp - B.timestamp) / 1000
	return distance / time
}

// метод получения двух крайних точек, на прямой,
// полученной методом апроксимации по точкам - регрессия 1 порядка
export const getAverage = (...points: Coordinate[]): [Coordinate, Coordinate] | undefined => {
  if (points.length < 2) {
    return
  }
  const x = points.map(p => p[0]).filter(p => isNumber(p))
  const y = points.map(p => p[1]).filter(p => isNumber(p))
  if (x.length !== y.length) {
    console.error(x, y, "Массивы точек разного размера.")
    return
  }
  const poly = new Polyfit(x, y)
  const func = poly.getPolynomial(1)
  const Ax = x[0] > x[x.length - 1] ? Math.max(...x) : Math.min(...x)
  const Bx = x[0] > x[x.length - 1] ? Math.min(...x) : Math.max(...x)
  return [[Ax, func(Ax)], [Bx, func(Bx)]]
}

// метод определяющий, надо ли поворачивать какой-либо объект
export const needRotate = (rotation: number, curRotation?: number): boolean => {
  if (!curRotation && curRotation !== 0) return true
  const diff = Math.min(
    Math.abs(rotation - curRotation),
    Math.abs(2 * Math.PI - Math.abs(curRotation - rotation))
  )
  return diff > 0.15 || (rotation === 0 && curRotation !== 0)
}

export const wait = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export const useDidMountEffect = (func: EffectCallback, deps: DependencyList) => {
  const didMount = useRef(false)
  useEffect(() => {
    if (didMount.current) func()
    else didMount.current = true
  }, deps)
}

export const usePrevious = <T>(value: T): T | undefined => {
  const previous = useRef<T>();
  useEffect(() => {
    previous.current = value;
  });
  return previous.current;
};

export const splitLineString = (
  geometry: LineString,
  minSegmentLength: number, // число в метрах
  options?: {alwaysUp?: boolean, midPoints?: boolean}
): Coordinate[] => {

  const calculatePointsDistance = (A: Coordinate, B: Coordinate) => {
    var dx = A[0] - B[0]
    var dy = A[1] - B[1]
    return Math.sqrt(dx * dx + dy * dy)
  }

  const calculateSplitPointCoords = (
    startNode: Coordinate,
    nextNode: Coordinate,
    distanceBetweenNodes: number,
    distanceToSplitPoint: number
  ) => {
    var d = distanceToSplitPoint / distanceBetweenNodes
    var x = nextNode[0] + (startNode[0] - nextNode[0]) * d
    var y = nextNode[1] + (startNode[1] - nextNode[1]) * d
    return [x, y]
  }

  const calculateAngle = (
    startNode: Coordinate,
    nextNode: Coordinate,
    alwaysUp?: boolean
  ) => {
    var x = (startNode[0] - nextNode[0])
    var y = (startNode[1] - nextNode[1])
    var angle = Math.atan(x/y)
    if (!alwaysUp) {
       angle = y > 0 ? angle + Math.PI : x < 0 ? angle + Math.PI*2 : angle
    }
    return angle
  }

  const splitPoints: Coordinate[] = []
  const coords = geometry.getCoordinates()

  let coordIndex = 0
  let startPoint = coords[coordIndex]
  let nextPoint = coords[coordIndex + 1]
  let angle = calculateAngle(startPoint, nextPoint, options?.alwaysUp)

  // 3600 - это кол-во секунд в градусе, 31 метр в секунде
  const n = Math.ceil((geometry.getLength() * 3600 * 31) /minSegmentLength)
  const segmentLength = geometry.getLength() / n
  let currentSegmentLength = options?.midPoints ? segmentLength/2 : segmentLength

  for (let i = 0; i <= n; i++) {
    const distanceBetweenPoints = calculatePointsDistance(startPoint, nextPoint)
    currentSegmentLength += distanceBetweenPoints

    if (currentSegmentLength < segmentLength) {
      coordIndex++
      if (coordIndex < coords.length - 1) {
        startPoint = coords[coordIndex]
        nextPoint = coords[coordIndex + 1]
        angle = calculateAngle(startPoint, nextPoint, options?.alwaysUp)
        i--
      } else {
        if (!options?.midPoints) {
          const splitPointCoords = nextPoint
          splitPointCoords.push(angle)
          splitPoints.push(splitPointCoords)
        }
        break
      }
    } else {
      const distanceToSplitPoint = currentSegmentLength - segmentLength
      const splitPointCoords = calculateSplitPointCoords(startPoint, nextPoint, distanceBetweenPoints, distanceToSplitPoint)
      startPoint = splitPointCoords.slice()
      splitPointCoords.push(angle)
      splitPoints.push(splitPointCoords)
      currentSegmentLength = 0
    }
  }

  return splitPoints
}

export const replaceDigitsExceptFirst = (num: number): number => {
  const numStr = String(num)
  let start = numStr[0]
  let zeroCount = numStr.length - 1
  if (numStr.length > 1 && Number(numStr[1]) >= 5) {
    start += "5"
    zeroCount--
  }
  return Number(start + '0'.repeat(zeroCount))
}
